import { defineMessages } from 'react-intl';

import { GlobalStyleConstants } from '../../constants/Styling';
import { type ValueOf } from '../../types';

import * as D from './deps';

export const DOMAIN_NAME = 'collection';

export const CollectionKeys = {
  ARTISTS_ITEMS_ID_LIST: 'ARTISTS_ITEMS_ID_LIST',
  ARTISTS_LIST: 'ARTISTS_LIST',
  COLLECTION: 'COLLECTION',
  CURRENT_FILTER: 'CURRENT_FILTER',
  CURRENT_OFFSET: 'CURRENT_OFFSET',
  CURRENT_VIEWED_OFFSET: 'CURRENT_VIEWED_OFFSET',
  CURSOR: 'CURSOR',
  FETCHED_DATA_SUCCESS_KEY: 'FETCHED_DATA_SUCCESS_KEY',
  FETCHED_OFFSETS: 'FETCHED_OFFSETS',
  FETCHING_OFFSETS: 'FETCHING_OFFSETS',
  HAS_MET_RECENTLY_PLAYED_THRESHOLD: 'HAS_MET_RECENTLY_PLAYED_THRESHOLD',
  IS_CURRENTLY_SYNCING: 'IS_CURRENTLY_SYNCING',
  ITEMS: 'ITEMS',
  PREVIOUS_OFFSET: 'PREVIOUS_OFFSET',
  PREVIOUS_TOTAL_COUNT: 'PREVIOUS_TOTAL_COUNT',
  PREVIOUS_VERSION: 'PREVIOUS_VERSION',
  RECENTLY_PLAYED_PODCASTS: 'RECENTLY_PLAYED_PODCASTS',
  RECENTLY_PLAYED_SPS_ITEMS: 'RECENTLY_PLAYED_SPS_ITEMS',
  SHOULD_RELOAD: 'SHOULD_RELOAD',
  SHOULD_RELOAD_ARTIST_VIEW: 'SHOULD_RELOAD_ARTIST_VIEW',
  SORT: 'SORT',
  SORTED_FILTERS: 'SORTED_FILTERS',
  TOTAL_COUNT: 'TOTAL_COUNT',
  VERSION: 'VERSION',
} as const;

export const CollectionActions = {
  ADD_RECENTLY_PLAYED_PODCASTS: 'ADD_RECENTLY_PLAYED_PODCASTS',
  LOAD_FILTER_AT_CURRENT_OFFSET: 'LOAD_FILTER_AT_CURRENT_OFFSET',
  OPEN_PREMIUM_WELCOME_MODAL: 'OPEN_PREMIUM_WELCOME_MODAL',
  REMOVE_RECENTLY_PLAYED_ITEMS: 'REMOVE_RECENTLY_PLAYED_ITEMS',
  RESET_FILTER_OFFSETS: 'RESET_FILTER_OFFSETS',
  SET_FETCHING_OFFSETS: 'SET_FETCHING_OFFSETS',
  SET_FILTER_CURRENT_OFFSET: 'SET_FILTER_CURRENT_OFFSET',
  SET_HAS_MET_RECENTLY_PLAYED_THRESHOLD: 'SET_HAS_MET_RECENTLY_PLAYED_THRESHOLD',
  SET_RECENTLY_PLAYED_SPS_ITEMS: 'SET_RECENTLY_PLAYED_SPS_ITEMS',
} as const;

export const FilterTypes = {
  ALBUMS: 'albums',
  ALL: 'all',
  ARTISTS: 'artists',
  EPISODES: 'episodes',
  PLAYLISTS: 'playlists',
  PODCASTS: 'podcasts',
  SONGS: 'songs',
  STATIONS: 'stations',
} as const;

export type CollectionActionsFilterTypes = ValueOf<typeof FilterTypes>;

export const CollectionSorts = Object.freeze({
  MOST_RECENT_ADDED: 'MOST_RECENT_ADDED',
  RECENT_MODIFIED: 'MOST_RECENT_MODIFIED',
  RECENT_MODIFIED_PLAYLISTS: 'RECENT_MODIFIED_PLAYLISTS',
} as const);

export const PandoraTypeToFilterTypeMap = {
  [D.PandoraTypes.ALBUM]: FilterTypes.ALBUMS,
  [D.PandoraTypes.ARTIST]: FilterTypes.ARTISTS,
  [D.PandoraTypes.PLAYLIST]: FilterTypes.PLAYLISTS,
  [D.PandoraTypes.SONG]: FilterTypes.SONGS,
} as const;

export const INITIAL_LOAD_LIMIT = 1000;
export const ANNOTATION_LIMIT = 100;
export const SORTED_ITEMS_LIMIT = 40;
export const RECOMMENDATIONS_MAX_LIST_SIZE = 8;
export const RECENTLY_PLAYED_PLAYBACK_TIMER_THRESHOLD = 15;
export const RECENTLY_PLAYED_PLACEHOLDER_THRESHOLD = 4;
export const RECENTLY_PLAYED_MAX_LIST_SIZE = 10;
export const RECENTLY_PLAYED_PODCASTS_MAX_LIST_SIZE = 5;
export const RECENTLY_PLAYED_SPREAD_ACTIVATION_THRESHOLD = 0.6;
export const RECENTLY_PLAYED_THROTTLE = 250;
export const RECENTLY_PLAYED_TRANSITION_DURATION = 700;
export const RECENTLY_PLAYED_PLACEHOLDER_BACKGROUND_COLOR = 'rgb(244,244,244)';
export const TOP_NAV_HEIGHT = GlobalStyleConstants.TOP_BAR_HEIGHT;
export const TOP_NAV_HEIGHT_SMALLER_VIEWPORT = GlobalStyleConstants.TOP_BAR_HEIGHT_MIN_WIDTH;
export const GET_SORTED_BY_TYPES_ALL = [
  D.PandoraTypes.USER_STATION,
  D.PandoraTypes.PODCAST,
  D.PandoraTypes.EPISODE,
];
export const GET_SORTED_BY_TYPES_ALL_PREMIUM = ['ALL'];

export const RECENTLY_PLAYED_SPS_TYPES = [
  D.PandoraTypes.ALBUM,
  D.PandoraTypes.ARTIST_TOP_SONGS,
  D.PandoraTypes.PLAYLIST,
  D.PandoraTypes.PODCAST,
  D.PandoraTypes.USER_STATION,
  D.PandoraTypes.SONG,
];

export const messages = defineMessages({
  recentlyPlayedTitle: {
    id: 't.recentlyPlayedTitle',
    defaultMessage: 'Recently Played',
  },
  recentlyPlayedPlaceholderText: {
    id: 't.recentlyPlayedPlaceholderText',
    defaultMessage: 'You will see your recently played music here.',
  },
} as const);

export const CollectionFilterTypes = Object.freeze({
  [D.MyMusicSorts.A_TO_Z]: D.MyCollectedStationSorts.A_TO_Z,
  [D.MyMusicSorts.RECENT]: D.MyCollectedStationSorts.RECENTLY_COLLECTED,
} as const);

export const MyStationsDisplayToSort = Object.freeze({
  [D.MyStationsSorts.A_TO_Z]: D.MyStationListSorts.alphabetic,
  [D.MyStationsSorts.MOST_LISTENED]: D.MyStationListSorts.playTime,
} as const);

export const MyStationsCollectionToSort = Object.freeze({
  [D.MyStationsSorts.A_TO_Z]: D.MyStationListSorts.alphabetic,
  [D.MyStationsSorts.MOST_LISTENED]: D.MyStationListSorts.playTime,
  [D.MyStationsSorts.RECENT]: D.MyStationListSorts.recency,
  [D.MyCollectedStationSorts.RECENTLY_COLLECTED]: D.MyStationListSorts.createdTime,
} as const);

export const MyPodcastsCollectionToSort = Object.freeze({
  [D.MyPodcastSorts.RECENTLY_COLLECTED]: D.MyPodcastSorts.RECENTLY_COLLECTED,
  [D.MyPodcastSorts.A_TO_Z]: D.MyPodcastSorts.A_TO_Z,
} as const);

export const AllTypes = Object.freeze([
  D.PandoraTypes.ALBUM,
  D.PandoraTypes.PLAYLIST,
  D.PandoraTypes.SONG,
] as const);

export const PodcastTypes = Object.freeze([D.PandoraTypes.PODCAST, D.PandoraTypes.EPISODE]);

export default CollectionKeys;
